import { z } from "zod";

const variantSchema = z.object({
  key: z
    .string()
    .min(1, "variant key는 필수입니다")
    .regex(/^[a-z0-9_]+$/, "소문자, 숫자, 밑줄만 허용됩니다"),
  weight: z
    .number()
    .int("정수만 허용됩니다")
    .min(0, "0 이상이어야 합니다")
    .max(10000, "10000 이하여야 합니다"),
});

const targetingSchema = z
  .object({
    newUsersOnly: z.boolean().default(false),
    platforms: z
      .array(z.enum(["web", "mobile", "desktop"]))
      .default([]),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.newUsersOnly && !data.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "신규 유저 필터 사용 시 시작일이 필요합니다",
        path: ["startDate"],
      });
    }

    if (data.startDate && data.endDate && data.startDate >= data.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "종료일은 시작일 이후여야 합니다",
        path: ["endDate"],
      });
    }
  })
  .optional();

const variantsWeightRefine = {
  refinement: (data: { variants: { weight: number }[] }) => {
    const total = data.variants.reduce((sum, v) => sum + v.weight, 0);
    return total === 10000;
  },
  options: {
    message: "variant weight 합계는 10000이어야 합니다",
    path: ["variants"] as string[],
  },
};

const baseExperimentFields = {
  name: z.string().min(1, "실험명은 필수입니다"),
  description: z.string().optional(),
  variants: z
    .array(variantSchema)
    .min(2, "최소 2개의 variant가 필요합니다")
    .superRefine((variants, ctx) => {
      const keys = variants.map((v) => v.key);
      if (new Set(keys).size !== keys.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "중복된 variant key가 있습니다",
        });
      }
    }),
  targeting: targetingSchema,
};

export const createExperimentSchema = z
  .object({
    key: z
      .string()
      .min(1, "실험 key는 필수입니다")
      .regex(/^[a-z0-9_]+$/, "소문자, 숫자, 밑줄만 허용됩니다"),
    ...baseExperimentFields,
  })
  .refine(variantsWeightRefine.refinement, variantsWeightRefine.options);

export const updateExperimentSchema = z
  .object(baseExperimentFields)
  .refine(variantsWeightRefine.refinement, variantsWeightRefine.options);

export const updateExperimentRolloutSchema = z
  .object({
    variants: baseExperimentFields.variants,
  })
  .refine(variantsWeightRefine.refinement, variantsWeightRefine.options);

export type CreateExperimentFormValues = z.infer<typeof createExperimentSchema>;
export type UpdateExperimentFormValues = z.infer<typeof updateExperimentSchema>;
export type UpdateExperimentRolloutFormValues = z.infer<
  typeof updateExperimentRolloutSchema
>;
